import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sidebar,
  MainContent,
  ContentCard,
  StatCard,
  Icons
} from './ModernSidebarLayout';
import {
  subscribeToCompanies,
  addCompany,
  updateCompany,
  deleteCompany,
  toggleCompanyActive,
  toggleCompanyPublished,
  getPreparersByCompany
} from '../firestoreHelper';
import { getTemplatesList } from '../landingPageTemplates';
import { isVapidKeyConfigured, isPushSupported } from '../pushNotificationService';

// Menu items for Owner Dashboard
const getOwnerMenuItems = (counts = {}) => [
  {
    id: 'companies',
    label: 'Companies',
    icon: Icons.Dashboard,
    badge: counts.companies || 0
  },
  {
    id: 'templates',
    label: 'Templates',
    icon: Icons.Document,
    badge: counts.templates || 0
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Icons.Profile
  },
  {
    id: 'divider-1',
    type: 'divider',
    label: 'Actions'
  },
  {
    id: 'add-company',
    label: 'Add Company',
    icon: Icons.Plus
  }
];

function OwnerDashboard({ onLogout, firebaseUser, onLoginAsAdmin, onLoginAsPreparer }) {
  const [companies, setCompanies] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPreparersModal, setShowPreparersModal] = useState(false);
  const [selectedCompanyPreparers, setSelectedCompanyPreparers] = useState([]);
  const [selectedCompanyForPreparers, setSelectedCompanyForPreparers] = useState(null);
  const [loadingPreparers, setLoadingPreparers] = useState(false);
  
  // Responsive states
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isMobile = windowWidth < 1024;
  const isSmallMobile = windowWidth < 400;
  
  // Modern sidebar states
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyPreparerCounts, setCompanyPreparerCounts] = useState({});
  const [showPasswordFor, setShowPasswordFor] = useState({});
  const [editCompanyPreparers, setEditCompanyPreparers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('companies'); // 'companies' or 'templates'
  const [selectedTemplatePreview, setSelectedTemplatePreview] = useState(null);
  const [templateCategory, setTemplateCategory] = useState('all'); // Template category filter
  const [platformLogo, setPlatformLogo] = useState(() => {
    // Load from localStorage or use default
    return localStorage.getItem('taxfilePro_platformLogo') || '/taxfile-pro-logo.png';
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    logo: '',
    description: '',
    adminUsername: '',
    adminPassword: '',
    primaryColor: '#2563eb',
    secondaryColor: '#1e40af',
    templateId: 'taxfile' // Default template
  });

  // Get all templates
  const templates = getTemplatesList();

  // Subscribe to companies
  useEffect(() => {
    const unsubscribe = subscribeToCompanies((data) => {
      setCompanies(data);
      // Fetch preparer counts for each company
      data.forEach(async (company) => {
        const preparers = await getPreparersByCompany(company.id);
        setCompanyPreparerCounts(prev => ({
          ...prev,
          [company.id]: preparers.length
        }));
      });
    });
    return () => unsubscribe();
  }, []);

  // Window resize listener for responsive design
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.adminUsername.trim() || !formData.adminPassword.trim()) {
      alert('Please fill in Company Name, Admin Username, and Admin Password');
      return;
    }

    try {
      await addCompany(formData);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        logo: '',
        description: '',
        adminUsername: '',
        adminPassword: '',
        primaryColor: '#2563eb',
        secondaryColor: '#1e40af',
        templateId: 'taxfile'
      });
      setShowAddForm(false);
      alert('✅ Company created successfully!');
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleEditCompany = async (company) => {
    setEditingCompany(company);
    const newFormData = {
      name: company.name || '',
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || '',
      logo: company.logo || '',
      description: company.description || '',
      adminUsername: company.adminUsername || '',
      adminPassword: company.adminPassword || '',
      primaryColor: company.primaryColor || '#2563eb',
      secondaryColor: company.secondaryColor || '#1e40af',
      templateId: company.templateId || 'taxfile'
    };
    setFormData(newFormData);
    // Load preparers for this company
    const preparers = await getPreparersByCompany(company.id);
    setEditCompanyPreparers(preparers);
    setShowEditForm(true);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.adminUsername.trim() || !formData.adminPassword.trim()) {
      alert('Please fill in Company Name, Admin Username, and Admin Password');
      return;
    }

    try {
      await updateCompany(editingCompany.id, formData);
      setShowEditForm(false);
      setEditingCompany(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        logo: '',
        description: '',
        adminUsername: '',
        adminPassword: '',
        primaryColor: '#2563eb',
        secondaryColor: '#1e40af',
        templateId: 'taxfile'
      });
      alert('✅ Company updated successfully!');
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleDeleteCompany = async (company) => {
    if (window.confirm(`Are you sure you want to delete "${company.name}"? This will also remove all associated preparers and data.`)) {
      try {
        await deleteCompany(company.id);
        alert('✅ Company deleted successfully!');
      } catch (error) {
        alert(`❌ Error: ${error.message}`);
      }
    }
  };

  const handleToggleActive = async (company) => {
    try {
      await toggleCompanyActive(company.id, !company.active);
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleTogglePublished = async (company) => {
    try {
      await toggleCompanyPublished(company.id, !company.published);
      if (!company.published) {
        alert(`✅ "${company.name}" is now published!\n\nLanding Page URL:\n${window.location.origin}/company/${company.slug}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const copyCompanyUrl = (slug) => {
    const url = `${window.location.origin}/company/${slug}`;
    navigator.clipboard.writeText(url);
    alert('✅ Company URL copied to clipboard!');
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Logo size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.adminUsername?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // If editing a company, show the full page edit form
  if (showEditForm && editingCompany) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        {/* Edit Header */}
        <header style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
          color: 'white',
          padding: isMobile ? '16px 20px' : '24px 32px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '12px' : '20px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: '700' }}>
              ✏️ Editing: {editingCompany.name}
            </h1>
            <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: '0.9rem' }}>Update company information, branding, and staff</p>
          </div>
          <button 
            onClick={() => {
              setShowEditForm(false);
              setEditingCompany(null);
              setEditCompanyPreparers([]);
            }}
            style={{
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              padding: isMobile ? '12px 20px' : '12px 24px',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(4px)'
            }}
          >
            ← Back to Dashboard
          </button>
        </header>

        {/* Edit Form Content */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px' }}>
          {/* Company URL Info */}
          <div style={{ background: '#dbeafe', padding: '15px 20px', borderRadius: '10px', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.5rem' }}>🔗</span>
            <div>
              <strong>Landing Page URL:</strong>
              <code style={{ background: 'white', padding: '5px 10px', borderRadius: '6px', marginLeft: '10px', fontSize: '0.9rem' }}>
                {window.location.origin}/company/{editingCompany.slug}
              </code>
            </div>
          </div>

          <form onSubmit={handleUpdateCompany}>
            {/* Main Grid - Two Columns */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))', gap: '25px', marginBottom: '25px' }}>
              
              {/* Left Column - Company Information */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <h2 style={{ margin: '0 0 20px', fontSize: '1.2rem', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '10px' }}>
                  📋 Company Information
                </h2>
                
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                    <span style={{ color: '#ef4444' }}>*</span> Company Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., ABC Tax Services"
                    required
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>📧 Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="contact@company.com"
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>📞 Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="123-456-7890"
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>📍 Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Main St, City, State"
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>📝 Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the company..."
                    rows="4"
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Right Column - Admin Credentials & Branding */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                
                {/* Admin Credentials */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                  <h2 style={{ margin: '0 0 20px', fontSize: '1.2rem', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '10px' }}>
                    🔐 Admin Credentials
                  </h2>
                  
                  <div style={{ marginBottom: '18px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                      <span style={{ color: '#ef4444' }}>*</span> Admin Username
                    </label>
                    <input
                      type="text"
                      name="adminUsername"
                      value={formData.adminUsername}
                      onChange={handleInputChange}
                      placeholder="admin_username"
                      required
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ marginBottom: '0' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                      <span style={{ color: '#ef4444' }}>*</span> Admin Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPasswordFor['editCompany'] ? "text" : "password"}
                        name="adminPassword"
                        value={formData.adminPassword}
                        onChange={handleInputChange}
                        placeholder="Enter password"
                        required
                        style={{ width: '100%', padding: '12px', paddingRight: '50px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', boxSizing: 'border-box' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswordFor(prev => ({ ...prev, editCompany: !prev.editCompany }))}
                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem' }}
                      >
                        {showPasswordFor['editCompany'] ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Branding */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                  <h2 style={{ margin: '0 0 20px', fontSize: '1.2rem', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '10px' }}>
                    🎨 Branding
                  </h2>
                  
                  <div style={{ marginBottom: '18px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>🖼️ Company Logo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', boxSizing: 'border-box' }}
                    />
                    {formData.logo && (
                      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={formData.logo} alt="Logo preview" style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, logo: '' }))} style={{ padding: '6px 12px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>Primary Color</label>
                      <input
                        type="color"
                        name="primaryColor"
                        value={formData.primaryColor}
                        onChange={handleInputChange}
                        style={{ width: '100%', height: '50px', borderRadius: '8px', border: '1px solid #d1d5db', cursor: 'pointer' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>Secondary Color</label>
                      <input
                        type="color"
                        name="secondaryColor"
                        value={formData.secondaryColor}
                        onChange={handleInputChange}
                        style={{ width: '100%', height: '50px', borderRadius: '8px', border: '1px solid #d1d5db', cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Preparers Section */}
            {editCompanyPreparers.length > 0 && (
              <div style={{ background: '#fef3c7', borderRadius: '12px', padding: '25px', marginBottom: '25px' }}>
                <h2 style={{ margin: '0 0 20px', fontSize: '1.2rem', color: '#92400e' }}>
                  👥 Preparers ({editCompanyPreparers.length})
                </h2>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {editCompanyPreparers.map((preparer) => (
                    <div key={preparer.id} style={{
                      background: 'white',
                      padding: '15px 20px',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '15px'
                    }}>
                      <div>
                        <strong style={{ fontSize: '1rem' }}>{preparer.name}</strong>
                        <span style={{ color: '#6b7280', marginLeft: '10px' }}>@{preparer.username}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontFamily: 'monospace', background: '#f3f4f6', padding: '6px 12px', borderRadius: '6px' }}>
                          {showPasswordFor[`preparer_${preparer.id}`] ? preparer.password : '••••••••'}
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowPasswordFor(prev => ({ ...prev, [`preparer_${preparer.id}`]: !prev[`preparer_${preparer.id}`] }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem' }}
                        >
                          {showPasswordFor[`preparer_${preparer.id}`] ? '👁️' : '👁️‍🗨️'}
                        </button>
                        <span style={{
                          background: preparer.active !== false ? '#dcfce7' : '#fee2e2',
                          color: preparer.active !== false ? '#166534' : '#991b1b',
                          padding: '5px 12px',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          {preparer.active !== false ? '✅ Active' : '❌ Inactive'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Template Selection */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
              <h2 style={{ margin: '0 0 10px', fontSize: '1.2rem', color: '#1f2937' }}>🎨 Landing Page Template</h2>
              <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '0.9rem' }}>Choose a template that best fits your business type</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '15px' }}>
                {templates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => setFormData(prev => ({ ...prev, templateId: template.id }))}
                    style={{
                      padding: '18px 15px',
                      borderRadius: '12px',
                      border: formData.templateId === template.id ? '3px solid #2563eb' : '2px solid #e5e7eb',
                      background: formData.templateId === template.id ? '#eff6ff' : 'white',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '12px',
                      background: `linear-gradient(135deg, ${template.primaryColor}, ${template.secondaryColor})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 10px',
                      fontSize: '1.5rem'
                    }}>
                      {template.icon}
                    </div>
                    <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#1f2937' }}>{template.name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '4px' }}>{template.category}</div>
                    {formData.templateId === template.id && (
                      <div style={{ marginTop: '8px', color: '#2563eb', fontWeight: '600', fontSize: '0.85rem' }}>✓ Selected</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* QR Code */}
            {editingCompany.published && editingCompany.slug && (
              <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '25px', marginBottom: '25px', textAlign: 'center' }}>
                <h2 style={{ margin: '0 0 15px', fontSize: '1.2rem', color: '#166534' }}>📱 Company QR Code</h2>
                <div style={{ display: 'inline-block', padding: '15px', background: 'white', borderRadius: '12px' }}>
                  <QRCodeSVG value={`${window.location.origin}/company/${editingCompany.slug}`} size={150} />
                </div>
                <p style={{ color: '#166534', marginTop: '10px', fontSize: '0.9rem' }}>
                  Scan to visit the company landing page
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
              <button
                type="button"
                onClick={() => {
                  setShowEditForm(false);
                  setEditingCompany(null);
                  setEditCompanyPreparers([]);
                }}
                style={{ padding: '14px 30px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{ padding: '14px 30px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}
              >
                ✅ Update Company
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Menu counts
  const menuCounts = {
    companies: companies.length,
    templates: templates.length
  };

  const menuItems = getOwnerMenuItems(menuCounts);

  // Handle menu item click
  const handleMenuClick = (itemId) => {
    if (itemId === 'add-company') {
      setShowAddForm(true);
    } else {
      setActiveTab(itemId);
    }
  };

  // Footer buttons
  const footerButtons = [
    {
      label: 'Logout',
      icon: Icons.Logout,
      onClick: onLogout
    }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        isMobile={isMobile}
        menuItems={menuItems}
        activeItem={activeTab}
        onItemClick={handleMenuClick}
        footerButtons={footerButtons}
        userInfo={{
          name: firebaseUser?.email || 'Platform Owner',
          role: 'Super Administrator',
          initials: '👑'
        }}
        headerInfo={{
          logo: <span style={{ fontSize: '22px' }}>👑</span>,
          title: 'TaxFile Pro',
          subtitle: 'Owner Portal'
        }}
      />

      {/* Main Content */}
      <MainContent
        isSidebarCollapsed={isSidebarCollapsed}
        isMobile={isMobile}
        onMenuClick={() => setIsSidebarOpen(true)}
        headerTitle="Platform Management"
        headerSubtitle={isMobile ? `${companies.length} Companies` : `Managing ${companies.length} Tax Companies`}
        companyInfo={{ 
          name: 'TaxFile Pro',
          logo: platformLogo
        }}
        headerActions={
          <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => setShowAddForm(true)}
              title="Add Company"
              style={{ 
                fontSize: isMobile ? '0.85rem' : '0.9rem', 
                padding: isMobile ? '10px 14px' : '10px 20px', 
                borderRadius: '10px', 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                color: 'white', 
                border: 'none', 
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)',
                minHeight: '44px',
                transition: 'all 0.2s ease'
              }}
            >
              <span style={{ fontSize: '1rem' }}>➕</span>
              {!isSmallMobile && <span>Add Company</span>}
            </button>
            <button 
              onClick={onLogout}
              title="Logout"
              style={{ 
                fontSize: isMobile ? '0.85rem' : '0.9rem', 
                padding: isMobile ? '10px 14px' : '10px 20px', 
                borderRadius: '10px', 
                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', 
                color: '#dc2626', 
                border: 'none', 
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minHeight: '44px',
                transition: 'all 0.2s ease'
              }}
            >
              <span>🚪</span>
              {!isSmallMobile && <span>Logout</span>}
            </button>
          </div>
        }
      >
        {/* Stats Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
          gap: isMobile ? '12px' : '16px', 
          marginBottom: isMobile ? '16px' : '24px' 
        }}>
          <StatCard
            title="Total Companies"
            value={companies.length}
            icon={() => <span style={{ fontSize: '16px' }}>🏢</span>}
            color="blue"
          />
          <StatCard
            title="Active Companies"
            value={companies.filter(c => c.active).length}
            icon={() => <span style={{ fontSize: '16px' }}>✅</span>}
            color="green"
          />
          <StatCard
            title="Published Pages"
            value={companies.filter(c => c.published).length}
            icon={() => <span style={{ fontSize: '16px' }}>🌐</span>}
            color="purple"
          />
          <StatCard
            title="Total Preparers"
            value={Object.values(companyPreparerCounts).reduce((sum, count) => sum + count, 0)}
            icon={() => <span style={{ fontSize: '16px' }}>👥</span>}
            color="orange"
          />
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {/* Companies Tab */}
            {activeTab === 'companies' && (
              <ContentCard>
                {/* Section Header with Search */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between', 
                  alignItems: isMobile ? 'stretch' : 'center', 
                  marginBottom: '20px', 
                  gap: '14px',
                  padding: '0 0 16px 0',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1e293b' }}>
                      🏢 Company Management
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                      {filteredCompanies.length} of {companies.length} companies shown
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexDirection: isMobile ? 'column' : 'row' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '1rem' }}>🔍</span>
                      <input
                        type="text"
                        placeholder="Search by name or admin..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ 
                          width: '100%', 
                          minWidth: isMobile ? '100%' : '280px',
                          padding: '12px 14px 12px 42px', 
                          borderRadius: '10px', 
                          border: '2px solid #e2e8f0', 
                          fontSize: '0.95rem',
                          boxSizing: 'border-box',
                          transition: 'border-color 0.2s ease'
                        }}
                      />
                    </div>
                    <button 
                      onClick={() => setShowAddForm(true)}
                      style={{ 
                        padding: '12px 20px', 
                        borderRadius: '10px', 
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', 
                        color: 'white', 
                        border: 'none', 
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        minHeight: '46px',
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span>➕</span> New Company
                    </button>
                  </div>
                </div>

      {/* Companies Table - Desktop */}
      <div style={{ 
        display: isMobile ? 'none' : 'block',
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)' }}>
                <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Company</th>
                <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Admin</th>
                <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: '700', color: '#374151', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Team</th>
                <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: '700', color: '#374151', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: '700', color: '#374151', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Landing</th>
                <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: '700', color: '#374151', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '60px 20px', textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🏢</div>
                    <p style={{ color: '#64748b', marginBottom: '16px' }}>No companies found</p>
                    <button 
                      onClick={() => setShowAddForm(true)}
                      style={{
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ➕ Add Your First Company
                    </button>
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((company, index) => (
                  <tr key={company.id} style={{ 
                    background: index % 2 === 0 ? 'white' : '#fafbfc',
                    transition: 'background 0.2s'
                  }}>
                    <td style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        {company.logo ? (
                          <img src={company.logo} alt={company.name} style={{ 
                            width: '48px', 
                            height: '48px', 
                            borderRadius: '12px', 
                            objectFit: 'cover',
                            border: '2px solid #e2e8f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                          }} />
                        ) : (
                          <div style={{ 
                            width: '48px', 
                            height: '48px', 
                            borderRadius: '12px', 
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontSize: '1.3rem',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                          }}>🏢</div>
                        )}
                        <div>
                          <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.95rem', marginBottom: '2px' }}>{company.name}</div>
                          <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{company.email || 'No email'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        padding: '6px 12px',
                        background: '#f1f5f9',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        color: '#475569'
                      }}>
                        👤 {company.adminUsername}
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 14px',
                        background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        color: '#1d4ed8'
                      }}>
                        👥 {companyPreparerCounts[company.id] || 0}
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                      <button
                        onClick={() => handleToggleActive(company)}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '20px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.8rem',
                          background: company.active ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)' : 'linear-gradient(135deg, #fee2e2, #fecaca)',
                          color: company.active ? '#166534' : '#991b1b',
                          transition: 'all 0.2s'
                        }}
                      >
                        {company.active ? '✅ Active' : '❌ Inactive'}
                      </button>
                    </td>
                    <td style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <button
                          onClick={() => handleTogglePublished(company)}
                          style={{
                            padding: '8px 14px',
                            borderRadius: '20px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.8rem',
                            background: company.published ? 'linear-gradient(135deg, #dbeafe, #bfdbfe)' : 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
                            color: company.published ? '#1d4ed8' : '#6b7280',
                            transition: 'all 0.2s'
                          }}
                        >
                          {company.published ? '🌐 Live' : '🔒 Draft'}
                        </button>
                        {company.published && company.slug && (
                          <>
                            <button
                              onClick={() => copyCompanyUrl(company.slug)}
                              title="Copy URL"
                              style={{
                                padding: '8px 10px',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                background: 'white',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                transition: 'all 0.2s'
                              }}
                            >
                              📋
                            </button>
                            <a
                              href={`/company/${company.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                padding: '8px 10px',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                background: 'white',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                transition: 'all 0.2s'
                              }}
                            >
                              🔗
                            </a>
                          </>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {onLoginAsAdmin && (
                          <button
                            onClick={() => onLoginAsAdmin(company)}
                            title="Login as Admin"
                            style={{
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: 'none',
                              background: 'linear-gradient(135deg, #10b981, #059669)',
                              color: 'white',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
                              transition: 'all 0.2s'
                            }}
                          >
                            🔑 Admin
                          </button>
                        )}
                        {onLoginAsPreparer && (
                          <button
                            onClick={async () => {
                              setLoadingPreparers(true);
                              setSelectedCompanyForPreparers(company);
                              try {
                                const preparers = await getPreparersByCompany(company.id);
                                setSelectedCompanyPreparers(preparers);
                                setShowPreparersModal(true);
                              } catch (error) {
                                console.error('Error fetching preparers:', error);
                                alert('Failed to load preparers');
                              }
                              setLoadingPreparers(false);
                            }}
                            title="View Preparers"
                            style={{
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: 'none',
                              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                              color: 'white',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              boxShadow: '0 2px 6px rgba(139, 92, 246, 0.3)',
                              transition: 'all 0.2s'
                            }}
                          >
                            👥 Preparers
                          </button>
                        )}
                        <button
                          onClick={() => handleEditCompany(company)}
                          title="Edit"
                          style={{
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)',
                            transition: 'all 0.2s'
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteCompany(company)}
                          title="Delete"
                          style={{
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#fee2e2',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.75rem',
                            transition: 'all 0.2s'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div style={{ display: isMobile ? 'block' : 'none' }}>
        {filteredCompanies.length === 0 ? (
          <div style={{ 
            background: 'white', 
            borderRadius: '16px', 
            padding: '60px 24px', 
            textAlign: 'center',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🏢</div>
            <p style={{ color: '#64748b', marginBottom: '16px' }}>No companies found</p>
            <button 
              onClick={() => setShowAddForm(true)}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ➕ Add Your First Company
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredCompanies.map(company => (
              <div key={company.id} style={{
                background: 'white',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
              }}>
                <div style={{ 
                  padding: '20px',
                  background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    {company.logo ? (
                      <img src={company.logo} alt={company.name} style={{ 
                        width: '44px', 
                        height: '44px', 
                        borderRadius: '10px', 
                        objectFit: 'cover',
                        border: '2px solid #e2e8f0'
                      }} />
                    ) : (
                      <div style={{ 
                        width: '44px', 
                        height: '44px', 
                        borderRadius: '10px', 
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: '1.2rem'
                      }}>🏢</div>
                    )}
                    <strong style={{ fontSize: '1rem', color: '#1e293b' }}>{company.name}</strong>
                  </div>
                  <span style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    background: company.active ? '#dcfce7' : '#fee2e2',
                    color: company.active ? '#166534' : '#991b1b'
                  }}>
                    {company.active ? '✅ Active' : '❌'}
                  </span>
                </div>
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>👤 Admin</span>
                    <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>{company.adminUsername}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>✉️ Email</span>
                    <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>{company.email || 'N/A'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>👥 Team</span>
                    <span style={{ fontWeight: '600', color: '#1d4ed8', fontSize: '0.85rem' }}>{companyPreparerCounts[company.id] || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>📄 Published</span>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      background: company.published ? '#dbeafe' : '#f3f4f6',
                      color: company.published ? '#1d4ed8' : '#6b7280'
                    }}>
                      {company.published ? '🌐 Yes' : '🔒 No'}
                    </span>
                  </div>
                </div>
                <div style={{ 
                  padding: '16px 20px', 
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  {onLoginAsAdmin && (
                    <button
                      onClick={() => onLoginAsAdmin(company)}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      🔑 Admin
                    </button>
                  )}
                  {onLoginAsPreparer && (
                    <button
                      onClick={async () => {
                        setLoadingPreparers(true);
                        setSelectedCompanyForPreparers(company);
                        try {
                          const preparers = await getPreparersByCompany(company.id);
                          setSelectedCompanyPreparers(preparers);
                          setShowPreparersModal(true);
                        } catch (error) {
                          console.error('Error fetching preparers:', error);
                          alert('Failed to load preparers');
                        }
                        setLoadingPreparers(false);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      👥 Preparers
                    </button>
                  )}
                  <button
                    onClick={() => handleEditCompany(company)}
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      background: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                      color: '#374151',
                      fontSize: '0.8rem'
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCompany(company)}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: 'none',
                      background: '#fee2e2',
                      color: '#dc2626',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
              </ContentCard>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
              <ContentCard>
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '1.35rem', fontWeight: '700', color: '#1e293b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.5rem' }}>🎨</span> Landing Page Templates
            </h2>
            <p style={{ fontSize: '0.9rem', color: '#64748b' }}>Choose from <strong style={{ color: '#2563eb' }}>{templates.length}</strong> professional templates • Click to preview</p>
          </div>
          
          {/* Category Filter Tabs */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px', 
            marginBottom: '24px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '12px'
          }}>
            {[
              { key: 'all', label: `All (${templates.length})`, icon: '' },
              { key: 'TaxFile', label: `TaxFile (${templates.filter(t => t.category === 'TaxFile').length})`, icon: '🏢' },
              { key: 'Bootstrap', label: `Bootstrap (${templates.filter(t => t.category === 'Bootstrap').length})`, icon: '🅱️' },
              { key: 'Gradient', label: `Gradient (${templates.filter(t => t.category === 'Gradient').length})`, icon: '🌈' },
              { key: 'Modern', label: `Modern (${templates.filter(t => t.category === 'Modern').length})`, icon: '💎' },
              { key: 'Corporate', label: `Corporate (${templates.filter(t => t.category === 'Corporate').length})`, icon: '🏛️' },
              { key: 'Creative', label: `Creative (${templates.filter(t => t.category === 'Creative').length})`, icon: '🎨' },
              { key: 'Nature', label: `Nature (${templates.filter(t => t.category === 'Nature').length})`, icon: '🌿' }
            ].map(cat => (
              <button
                key={cat.key}
                onClick={() => setTemplateCategory(cat.key)}
                style={{
                  padding: '10px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  transition: 'all 0.2s',
                  background: templateCategory === cat.key 
                    ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' 
                    : 'white',
                  color: templateCategory === cat.key ? 'white' : '#64748b',
                  boxShadow: templateCategory === cat.key 
                    ? '0 4px 12px rgba(37, 99, 235, 0.3)' 
                    : '0 1px 3px rgba(0,0,0,0.08)'
                }}
              >
                {cat.icon && <span style={{ marginRight: '6px' }}>{cat.icon}</span>}
                {cat.label}
              </button>
            ))}
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
            gap: '20px' 
          }}>
            {templates
              .filter(template => templateCategory === 'all' || template.category === templateCategory)
              .map(template => {
                const usageCount = companies.filter(c => c.templateId === template.id).length;
                const isSelected = selectedTemplatePreview === template.id;
                return (
                  <div 
                    key={template.id} 
                    onClick={() => setSelectedTemplatePreview(isSelected ? null : template.id)}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: isSelected 
                        ? '0 8px 25px rgba(37, 99, 235, 0.2)' 
                        : '0 4px 15px rgba(0,0,0,0.05)',
                      transform: isSelected ? 'translateY(-4px)' : 'none'
                    }}
                  >
                    {/* Icon Header */}
                    <div style={{ 
                      background: `linear-gradient(135deg, ${template.primaryColor}, ${template.secondaryColor})`,
                      padding: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      <span style={{ fontSize: '3rem', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>{template.icon}</span>
                      {usageCount > 0 && (
                        <span style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px',
                          background: 'rgba(255,255,255,0.95)',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '700',
                          color: '#2563eb'
                        }}>
                          {usageCount} in use
                        </span>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div style={{ padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <h3 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>{template.name}</h3>
                        <span style={{
                          padding: '4px 10px',
                          background: '#f1f5f9',
                          borderRadius: '6px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          color: '#64748b'
                        }}>{template.category}</span>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0 0 16px 0', lineHeight: '1.5' }}>{template.description}</p>
                      
                      {/* Color Swatches */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Colors:</span>
                        <div style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '6px', 
                          background: template.primaryColor,
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                        }} title="Primary"></div>
                        <div style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '6px', 
                          background: template.secondaryColor,
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                        }} title="Secondary"></div>
                      </div>
                      
                      {/* Expanded Preview */}
                      {isSelected && (
                        <div style={{ 
                          background: '#f8fafc', 
                          borderRadius: '12px', 
                          padding: '16px',
                          marginBottom: '16px'
                        }}>
                          <h4 style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Features</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                            {template.features.slice(0, 4).map((feature, idx) => (
                              <div key={idx} style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px',
                                padding: '8px 10px',
                                background: 'white',
                                borderRadius: '8px',
                                fontSize: '0.8rem'
                              }}>
                                <span>{feature.icon}</span>
                                <span style={{ color: '#475569' }}>{feature.title}</span>
                              </div>
                            ))}
                          </div>
                          
                          <h4 style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sample Services</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {template.services.slice(0, 3).map((service, idx) => (
                              <div key={idx} style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                background: 'white',
                                borderRadius: '8px',
                                fontSize: '0.8rem'
                              }}>
                                <span style={{ color: '#475569' }}>{service.name}</span>
                                <span style={{ fontWeight: '600', color: '#2563eb' }}>{service.price}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Action Button */}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({ 
                            ...prev, 
                            templateId: template.id,
                            primaryColor: template.primaryColor,
                            secondaryColor: template.secondaryColor
                          }));
                          setActiveTab('companies');
                          setShowAddForm(true);
                        }}
                        style={{
                          width: '100%',
                          padding: '12px 20px',
                          borderRadius: '10px',
                          border: 'none',
                          background: `linear-gradient(135deg, ${template.primaryColor}, ${template.secondaryColor})`,
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          boxShadow: `0 4px 12px ${template.primaryColor}40`,
                          transition: 'all 0.2s'
                        }}
                      >
                        ✨ Use This Template
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
              </ContentCard>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <ContentCard>
                <div style={{ padding: '20px' }}>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '8px', color: '#1e293b' }}>⚙️ Platform Settings</h2>
                  <p style={{ color: '#64748b', marginBottom: '24px' }}>Configure platform-wide settings and integrations</p>
                  
                  {/* Platform Logo Configuration */}
                  <div style={{ 
                    background: '#f8fafc', 
                    borderRadius: '16px', 
                    padding: '24px',
                    marginBottom: '20px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
                      <div style={{ 
                        width: '100px', 
                        height: '100px', 
                        borderRadius: '16px', 
                        background: 'white',
                        border: '2px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                      }}>
                        <img 
                          src={platformLogo} 
                          alt="TaxFile Pro Logo" 
                          style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                        <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>👑</div>
                      </div>
                      <div style={{ flex: 1, minWidth: '250px' }}>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 8px 0', color: '#1e293b' }}>
                          🎨 Platform Logo
                        </h3>
                        <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>
                          Upload your TaxFile Pro logo. This will appear in the dashboard header.
                        </p>
                        
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          <label style={{
                            padding: '10px 20px',
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            color: 'white',
                            borderRadius: '10px',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                            transition: 'all 0.2s'
                          }}>
                            📷 Upload Logo
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    const base64 = reader.result;
                                    setPlatformLogo(base64);
                                    localStorage.setItem('taxfilePro_platformLogo', base64);
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </label>
                          
                          {platformLogo !== '/taxfile-pro-logo.png' && (
                            <button
                              onClick={() => {
                                setPlatformLogo('/taxfile-pro-logo.png');
                                localStorage.removeItem('taxfilePro_platformLogo');
                              }}
                              style={{
                                padding: '10px 20px',
                                background: '#fee2e2',
                                color: '#dc2626',
                                borderRadius: '10px',
                                border: 'none',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                            >
                              ↩️ Reset to Default
                            </button>
                          )}
                        </div>
                        
                        <p style={{ margin: '12px 0 0 0', color: '#94a3b8', fontSize: '0.8rem' }}>
                          Recommended: PNG with transparent background, at least 200x200px
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Push Notifications Configuration */}
                  <div style={{ 
                    background: isVapidKeyConfigured() ? '#ecfdf5' : '#fefce8', 
                    borderRadius: '12px', 
                    padding: '20px',
                    marginBottom: '20px',
                    border: `1px solid ${isVapidKeyConfigured() ? '#a7f3d0' : '#fcd34d'}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                      <div style={{ 
                        width: '50px', 
                        height: '50px', 
                        borderRadius: '12px', 
                        background: isVapidKeyConfigured() ? '#10b981' : '#f59e0b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        flexShrink: 0
                      }}>
                        {isVapidKeyConfigured() ? '🔔' : '⚠️'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: '0 0 8px 0', color: isVapidKeyConfigured() ? '#047857' : '#92400e' }}>
                          Push Notifications {isVapidKeyConfigured() ? '- Configured ✓' : '- Setup Required'}
                        </h3>
                        <p style={{ margin: '0 0 12px 0', color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>
                          {isVapidKeyConfigured() 
                            ? 'Push notifications are configured. Preparers can now enable notifications to receive alerts when clients book appointments.'
                            : 'Push notifications are not configured yet. Preparers will not be able to receive booking alerts until this is set up.'}
                        </p>
                        
                        {!isVapidKeyConfigured() && (
                          <div style={{ 
                            background: 'white', 
                            borderRadius: '8px', 
                            padding: '16px',
                            border: '1px solid #fde68a'
                          }}>
                            <h4 style={{ fontSize: '0.95rem', fontWeight: '600', margin: '0 0 12px 0', color: '#1e293b' }}>
                              📋 Setup Instructions:
                            </h4>
                            <ol style={{ margin: '0', paddingLeft: '20px', color: '#374151', lineHeight: '2', fontSize: '0.9rem' }}>
                              <li>Go to <a href="https://console.firebase.google.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontWeight: '500' }}>Firebase Console</a></li>
                              <li>Select your project: <strong>taxfile-f201b</strong></li>
                              <li>Go to ⚙️ <strong>Project Settings</strong> → <strong>Cloud Messaging</strong> tab</li>
                              <li>Scroll to <strong>Web configuration</strong> section</li>
                              <li>Under <strong>Web Push certificates</strong>, click <strong>"Generate key pair"</strong></li>
                              <li>Copy the generated key (starts with "B...")</li>
                              <li>Open <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>src/firebase.js</code></li>
                              <li>Replace <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>YOUR_VAPID_KEY_HERE</code> with your key</li>
                              <li>Rebuild and deploy the app</li>
                            </ol>
                            
                            <div style={{ marginTop: '16px', padding: '12px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                              <p style={{ margin: 0, fontSize: '0.85rem', color: '#0369a1' }}>
                                <strong>💡 Tip:</strong> After setting the VAPID key, you also need to deploy Firebase Cloud Functions for push notifications to work when the app is closed. Run: <code style={{ background: '#e0f2fe', padding: '2px 4px', borderRadius: '3px' }}>firebase deploy --only functions</code>
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {isVapidKeyConfigured() && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            padding: '12px',
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid #a7f3d0'
                          }}>
                            <span style={{ color: '#10b981', fontSize: '1.2rem' }}>✅</span>
                            <span style={{ color: '#047857', fontSize: '0.9rem' }}>
                              VAPID key is configured. Push notifications are ready to use.
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Browser Support Info */}
                  <div style={{ 
                    background: '#f8fafc', 
                    borderRadius: '12px', 
                    padding: '20px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: '0 0 12px 0', color: '#374151' }}>
                      🌐 Browser Push Support
                    </h3>
                    <p style={{ margin: '0 0 12px 0', color: '#64748b', fontSize: '0.9rem' }}>
                      Current browser: {isPushSupported() 
                        ? <span style={{ color: '#10b981', fontWeight: '500' }}>✓ Supports push notifications</span>
                        : <span style={{ color: '#dc2626', fontWeight: '500' }}>✗ Does not support push notifications</span>
                      }
                    </p>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                      <p style={{ margin: '0 0 8px 0' }}>Push notifications work on:</p>
                      <ul style={{ margin: '0', paddingLeft: '20px', lineHeight: '1.8' }}>
                        <li>Chrome, Edge, Firefox (Desktop & Android)</li>
                        <li>Safari 16+ on macOS Ventura or later</li>
                        <li>iOS 16.4+ with app added to Home Screen (via PWA)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </ContentCard>
            )}
          </motion.div>
        </AnimatePresence>
      </MainContent>

      {/* Add Company Modal */}
      {showAddForm && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: isMobile ? 'flex-end' : 'center', 
          zIndex: 1000, 
          padding: isMobile ? '0' : '20px',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{ 
            width: '100%',
            maxWidth: isMobile ? '100%' : '900px', 
            maxHeight: isMobile ? '95vh' : '90vh',
            background: 'white', 
            borderRadius: isMobile ? '20px 20px 0 0' : '16px', 
            boxShadow: '0 20px 50px rgba(0,0,0,0.25)', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{ 
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', 
              padding: isMobile ? '16px 20px' : '20px 24px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexShrink: 0
            }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '600' }}>➕ Add New Company</h2>
              <button 
                onClick={() => setShowAddForm(false)} 
                style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  border: 'none', 
                  color: 'white', 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '50%', 
                  fontSize: '1.3rem', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}
              >×</button>
            </div>
            
            {/* Modal Body */}
            <div style={{ 
              padding: isMobile ? '16px' : '24px', 
              overflowY: 'auto', 
              flex: 1,
              paddingBottom: isMobile ? '100px' : '24px'
            }}>
              <form id="addCompanyForm" onSubmit={handleAddCompany}>
                {/* Form Grid */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
                  gap: isMobile ? '16px' : '24px' 
                }}>
                  {/* Left Column - Company Info */}
                  <div>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#374151', marginBottom: '14px', paddingBottom: '8px', borderBottom: '2px solid #e5e7eb' }}>📋 Company Information</h3>
                    
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>
                        <span style={{ color: '#ef4444' }}>*</span> Company Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., ABC Tax Services"
                        required
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>📧 Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="contact@company.com"
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>📞 Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="123-456-7890"
                          style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>📍 Address</label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="City, State"
                          style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>📝 Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Brief description..."
                        rows="3"
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {/* Right Column - Credentials & Branding */}
                  <div>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#374151', marginBottom: '14px', paddingBottom: '8px', borderBottom: '2px solid #e5e7eb' }}>🔐 Admin Credentials</h3>
                    
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>
                        <span style={{ color: '#ef4444' }}>*</span> Admin Username
                      </label>
                      <input
                        type="text"
                        name="adminUsername"
                        value={formData.adminUsername}
                        onChange={handleInputChange}
                        placeholder="admin_username"
                        required
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>
                        <span style={{ color: '#ef4444' }}>*</span> Admin Password
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showPasswordFor['newCompany'] ? "text" : "password"}
                          name="adminPassword"
                          value={formData.adminPassword}
                          onChange={handleInputChange}
                          placeholder="Enter password"
                          required
                          style={{ width: '100%', padding: '12px', paddingRight: '50px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', boxSizing: 'border-box' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswordFor(prev => ({ ...prev, newCompany: !prev.newCompany }))}
                          style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem' }}
                        >
                          {showPasswordFor['newCompany'] ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>

                    <h3 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#374151', marginBottom: '14px', paddingBottom: '8px', borderBottom: '2px solid #e5e7eb', marginTop: '20px' }}>🎨 Branding</h3>
                    
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>🖼️ Company Logo</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                      {formData.logo && (
                        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <img src={formData.logo} alt="Logo preview" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                          <button type="button" onClick={() => setFormData(prev => ({ ...prev, logo: '' }))} style={{ padding: '6px 12px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                            Remove
                          </button>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Primary Color</label>
                        <input
                          type="color"
                          name="primaryColor"
                          value={formData.primaryColor}
                          onChange={handleInputChange}
                          style={{ width: '100%', height: '44px', borderRadius: '8px', border: '1px solid #d1d5db', cursor: 'pointer' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Secondary Color</label>
                        <input
                          type="color"
                          name="secondaryColor"
                          value={formData.secondaryColor}
                          onChange={handleInputChange}
                          style={{ width: '100%', height: '44px', borderRadius: '8px', border: '1px solid #d1d5db', cursor: 'pointer' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Template Selection */}
                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>🎨 Landing Page Template</h3>
                  <p style={{ color: '#6b7280', marginBottom: '14px', fontSize: '0.85rem' }}>Choose a template that best fits your business</p>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(120px, 1fr))', 
                    gap: '10px' 
                  }}>
                    {templates.slice(0, isMobile ? 6 : templates.length).map(template => (
                      <div
                        key={template.id}
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          templateId: template.id,
                          primaryColor: prev.primaryColor === '#2563eb' ? template.primaryColor : prev.primaryColor,
                          secondaryColor: prev.secondaryColor === '#1e40af' ? template.secondaryColor : prev.secondaryColor
                        }))}
                        style={{
                          padding: '12px 10px',
                          borderRadius: '10px',
                          border: formData.templateId === template.id ? '3px solid #2563eb' : '2px solid #e5e7eb',
                          background: formData.templateId === template.id ? '#eff6ff' : 'white',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: `linear-gradient(135deg, ${template.primaryColor}, ${template.secondaryColor})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 8px',
                          fontSize: '1.2rem'
                        }}>
                          {template.icon}
                        </div>
                        <div style={{ fontWeight: '600', fontSize: '0.75rem', color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{template.name}</div>
                        {formData.templateId === template.id && (
                          <div style={{ marginTop: '4px', color: '#2563eb', fontWeight: '600', fontSize: '0.7rem' }}>✓ Selected</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </form>
            </div>
            
            {/* Modal Footer - Fixed on mobile */}
            <div style={{ 
              padding: isMobile ? '16px 20px' : '20px 24px',
              borderTop: '1px solid #e5e7eb',
              background: 'linear-gradient(to top, #f8fafc, white)',
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              ...(isMobile ? {
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
              } : {})
            }}>
              <button 
                type="button" 
                onClick={() => setShowAddForm(false)} 
                style={{ 
                  padding: isMobile ? '14px 24px' : '12px 24px', 
                  flex: isMobile ? 1 : 'none',
                  background: '#f1f5f9', 
                  color: '#475569', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '10px', 
                  fontSize: isMobile ? '1rem' : '0.95rem', 
                  fontWeight: '600', 
                  cursor: 'pointer',
                  minHeight: '48px',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              <button 
                type="submit"
                form="addCompanyForm"
                style={{ 
                  padding: isMobile ? '14px 24px' : '12px 24px', 
                  flex: isMobile ? 1 : 'none',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '10px', 
                  fontSize: isMobile ? '1rem' : '0.95rem', 
                  fontWeight: '600', 
                  cursor: 'pointer',
                  minHeight: '48px',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                ✅ Create Company
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preparers Modal */}
      {showPreparersModal && selectedCompanyForPreparers && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              padding: '20px 24px',
              color: 'white'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700' }}>
                    👥 {selectedCompanyForPreparers.name} - Preparers
                  </h2>
                  <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: '0.85rem' }}>
                    {selectedCompanyPreparers.length} preparer{selectedCompanyPreparers.length !== 1 ? 's' : ''} • Click to login as preparer
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPreparersModal(false);
                    setSelectedCompanyPreparers([]);
                    setSelectedCompanyForPreparers(null);
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '1.2rem'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px 24px', maxHeight: '50vh', overflowY: 'auto' }}>
              {loadingPreparers ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⏳</div>
                  <p style={{ color: '#64748b' }}>Loading preparers...</p>
                </div>
              ) : selectedCompanyPreparers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>👤</div>
                  <p style={{ color: '#64748b', marginBottom: '8px' }}>No preparers found for this company</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Add preparers from the Company Admin Dashboard</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedCompanyPreparers.map(preparer => (
                    <div
                      key={preparer.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        background: '#f8fafc',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        {preparer.photoURL ? (
                          <img 
                            src={preparer.photoURL} 
                            alt={preparer.name}
                            style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '12px',
                              objectFit: 'cover',
                              border: '2px solid #e2e8f0'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '1.1rem'
                          }}>
                            {preparer.name?.charAt(0)?.toUpperCase() || 'P'}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '2px' }}>
                            {preparer.name}
                            {preparer.isAdmin && (
                              <span style={{
                                marginLeft: '8px',
                                padding: '2px 8px',
                                background: '#dbeafe',
                                color: '#1d4ed8',
                                borderRadius: '6px',
                                fontSize: '0.7rem',
                                fontWeight: '600'
                              }}>Admin</span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            @{preparer.username}
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px',
                            marginTop: '4px'
                          }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '6px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              background: preparer.active ? '#dcfce7' : '#fee2e2',
                              color: preparer.active ? '#166534' : '#991b1b'
                            }}>
                              {preparer.active ? '✓ Active' : '✗ Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (onLoginAsPreparer) {
                            onLoginAsPreparer(preparer, selectedCompanyForPreparers);
                            setShowPreparersModal(false);
                            setSelectedCompanyPreparers([]);
                            setSelectedCompanyForPreparers(null);
                          }
                        }}
                        disabled={!preparer.active}
                        style={{
                          padding: '10px 16px',
                          borderRadius: '10px',
                          border: 'none',
                          background: preparer.active 
                            ? 'linear-gradient(135deg, #10b981, #059669)' 
                            : '#e2e8f0',
                          color: preparer.active ? 'white' : '#94a3b8',
                          cursor: preparer.active ? 'pointer' : 'not-allowed',
                          fontWeight: '600',
                          fontSize: '0.85rem',
                          boxShadow: preparer.active ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none',
                          transition: 'all 0.2s'
                        }}
                      >
                        🔑 Login
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowPreparersModal(false);
                  setSelectedCompanyPreparers([]);
                  setSelectedCompanyForPreparers(null);
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OwnerDashboard;
